import { updateIcons } from '../../utils/icons';
import { KEYCODE, SEARCH_URL } from '@/constants';

/**
 * Class definition for `wp-search-results` element
 *
 * @class WPSearchResults
 * @extends {HTMLElement}
 */
class WPSearchResults extends HTMLElement {
  constructor(self) {
    self = super(self);
    this._results = this.querySelector('.c-search-results__results');
    this._closeEl = this.querySelector('.c-search-results__close');

    return self;
  }

  /**
   * Tag name of element
   *
   * @readonly
   * @static
   */
  static get is() {
    return 'wp-search-results';
  }

  /**
   * Watched attributes
   *
   * @readonly
   * @static
   */
  static get observedAttributes() {
    return ['query'];
  }

  /**
   * Getter for `expanded` attribute
   */
  get expanded() {
    return this.hasAttribute('expanded');
  }

  /**
   * Setter for `expanded` attribute
   */
  set expanded(value) {
    if (value) {
      this.setAttribute('expanded', '');
    } else {
      this.removeAttribute('expanded');
    }
  }

  /**
   * Getter for `loading` attribute
   */
  get loading() {
    return this.hasAttribute('loading');
  }

  /**
   * Setter for `loading` attribute
   */
  set loading(value) {
    if (Boolean(value)) {
      this.setAttribute('loading', '');
    } else {
      this.removeAttribute('loading');
    }
  }

  /**
   * Getter for `query` attribute
   */
  get query() {
    return this.hasAttribute('query') ? this.getAttribute('query') : '';
  }

  /**
   * Setter for `query` attribute
   */
  set query(value) {
    if (String(value.length)) {
      this.setAttribute('query', value);
    } else {
      this.removeAttribute('query');
    }
  }

  connectedCallback() {
    this._bindHandlers();
    this.addEventListener('searchresultsclose', this._close, false);
  }

  /**
   * Lifecycle event when watched attribute is changed
   *
   * @param {string} name Attribute name
   * @param {any} oldVal Old value of attribute
   * @param {any} newVal New value of attribute
   */
  attributeChangedCallback(name, oldVal, newVal) {
    console.log('attributeChangedCallback', name, oldVal, newVal);
    switch (name) {
      case 'query':
        this._queryChanged(newVal);
    }
  }

  _bindHandlers() {
    this._handleKeyUp = this._handleKeyUp.bind(this);
  }

  async _getResults(query) {
    query = query.replace(/[^a-z0-9\-_ ]/i, ' ');
    const response = await fetch(SEARCH_URL + encodeURI(query));

    return await response.json();
  }

  _close(evt) {
    this.expanded = false;

    // Wait for animation to complete before removing search content and showing loader
    this._animate().then(() => {
      this.loading = true;
      document.body.classList.remove('search-is-open');
    });

    window.removeEventListener('keyup', this._handleKeyUp, false);

    // Return focus
    if (this.returnTarget) {
      this.returnTarget.focus();
      this.returnTarget = null;
    }
  }

  _queryChanged(query) {
    console.log('queryChanged', query);
    document.body.classList.add('search-is-open');
    this.$emit('searchresultsupdated', { query });
    this.expanded = true;
    this.loading = true;
    window.addEventListener('keyup', this._handleKeyUp, false);
    this._animate().then(() => {
      this._closeEl.focus();
    });

    const self = this;
    let content = this._startBuildSearch(query);

    if (query) {
      this._getResults(query)
        .then((data) => {
          const totalResults = data.length ? data.length : 0;

          content += `
            <p class="c-search-results__total">
              <span class="u-h4 u-no-m-t u-no-m-b">${totalResults} results</span>
            </p>
          `;

          // Results
          if (data && data.code !== 'No results') {
            if (data.length === 0) {
              content += `<p class="u-text-center">No results found, please try searching again.</p>`;
            } else {
              content += `<ol class="c-search-results__list">`;
              data.forEach((item) => {
                content += this._listItemTemplate(item);
              });
              content += `</ol>`;
            }

            // No results
          } else {
            content += `<p class="u-text-center">No results found, please try searching again.</p>`;
          }

          self._endBuildSearch(content);
        })
        .catch((error) => {
          console.log(error);
          self._endBuildSearch(content);
        });
    } else {
      this._endBuildSearch(content);
    }
  }

  _startBuildSearch(query) {
    const masthead = query ? `Search results for '${query}'` : `Search`;
    return `<h2 class="c-search-results__masthead">${masthead}</h2>
      <wp-search
        class="c-search c-search-results__form"
      >
        <form 
          class="c-search__form"
          role="search">
          <label for="s" class="u-visuallyhidden">Search Generation Victoria</label>
          <input 
            name="s"
            type="search" 
            class="c-search__input" 
            autocomplete="off"
            placeholder="Search"
            value="${query}">
          <button 
            type="submit" 
            class="c-search__button">
            <span class="u-visuallyhidden">Search</span>
            <i class="c-search__icon icon-search">
              <svg aria-hidden="true" width="20" height="20">
                <use xlink:href="/wp-content/themes/genv/static/dist/svg/symbols.svg#search" />
              </svg>
            </i>
          </button>
        </form>
      </wp-search>
    `;
  }

  _endBuildSearch(content) {
    content += `
      <wp-shape class="c-search-results__shape c-search-results__shape1" shape="dash" colour="accent2"></wp-shape>
      <wp-shape class="c-search-results__shape c-search-results__shape2" shape="cross" colour="accent5"></wp-shape>
    `;

    this.loading = false;
    this._results.innerHTML = content;
    updateIcons();
  }

  /**
   * Add event listener to ensure transition ends
   *
   * @private
   * @returns {Promise} Resolves when transitions ends
   */
  _animate() {
    return new Promise((resolve, reject) => {
      this.animating = true;
      this.$once('transitionend', () => {
        resolve();
      });

      setTimeout(() => {
        resolve();
      }, 500);
    });
  }

  /**
   * Handle keyup
   *
   * @param {UIEvent} evt
   * @private
   */
  _handleKeyUp(evt) {
    if (this.expanded && evt.keyCode === KEYCODE.ESC) {
      this._close();
    }
  }

  _listItemTemplate(item) {
    const excerpt = item.excerpt ? `<p>${item.excerpt.rendered}</p>` : '';

    return `<li class="c-search-results__item">
      <a href="${item.link}" class="c-search-results__link">
        <h3 class="c-search-results__title">
          ${item.title.rendered}
        </h3>
        <p class="c-search-results__url">${item.link}</p>
        ${excerpt}
        <p class="u-no-m"><span class="u-link-fake">Read more</span></p>
      </a>
    </li>`;
  }
}

customElements.define(WPSearchResults.is, WPSearchResults);
